import type { Department } from "../../contexts/AuthProvider";
import { ALLOW_AVAILABILITY_FALLBACK, byDimensionsOf, resolveLocationCensus, scoreAdjustedRequiredForLocation } from "../censusResolution";
import { demoRequiredForDay } from "../demoRequiredStaff";
import type { DemoRequiredInput } from "../hospitalDemoScore";
import type { DepartmentLocation, HospitalData, NurseInShift, StatsMode } from "../models";
import { filterNursesForCoverage } from "../statsCalculations";
import { getCensus } from "../utilFunctions";

/** A shift-day's nurse list honoring the actual/scheduled toggle (empty when the cell is absent). */
export const shiftNursesForDay = (day: { nurses: NurseInShift[]; nursesActual?: NurseInShift[] } | undefined, isActual: boolean): NurseInShift[] =>
	day ? (isActual ? (day.nursesActual ?? []) : day.nurses) : [];

/** How many coverage-counted nurses are already on `locationName` — the single source both the over-staffing
 * warning (which then adds the +1 it's projecting) and the picker's par pill (raw current) count from. */
export const countCoverageNursesOnLocation = (shiftNurses: NurseInShift[], locationName: string): number =>
	shiftNurses.filter((nurse) => nurse.location === locationName && filterNursesForCoverage(nurse, false)).length;

/**
 * The day-level inputs a location's staffing target is resolved against — census map, department total,
 * weekday abbreviation, and the demo-only adjustment (patient score or node split). All are shift-INDEPENDENT
 * (they key off the week + day), so they are computed once per day and reused for every location.
 *
 * Extracted so `useStaffingTargetCheck` (the over-staffing warning) and `useLocationParCounts` (the
 * location-picker par pills) resolve the identical number and can never drift.
 */
export type DayTargetContext = {
	byDimensions: Record<string, number>;
	totalCensus: number;
	dayAbbr: string;
	demoRequired: DemoRequiredInput;
};

export const computeDayTargetContext = (
	hospitalData: HospitalData,
	department: Department,
	weekIndex: number,
	dayNo: number,
	isHospitalView: boolean,
): DayTargetContext | null => {
	const week = hospitalData.weeks[weekIndex];
	if (!week) return null;
	// The date + census are the same for every shift on a given day, so the first shift's day carries them.
	const dayForDate = week.weeklyShifts[0]?.days[dayNo];
	if (!dayForDate) return null;
	return {
		byDimensions: byDimensionsOf(dayForDate.census_grouping),
		totalCensus: getCensus(week, dayNo),
		dayAbbr: dayForDate.date.setLocale("en").toFormat("ccc").toLowerCase(),
		demoRequired: demoRequiredForDay({ department, week, dayIndex: dayNo, isRealHospitalDemo: isHospitalView }),
	};
};

/**
 * The location TOTAL required (par) — summed across every same-named `DepartmentLocation` variant, with no
 * role/job filter — resolved against the day's census via the shared ratio-ladder math, with the same demo
 * adjustment Staffing uses so it matches the number shown there.
 */
export const computeLocationTotalTarget = (matchingLocations: DepartmentLocation[], context: DayTargetContext, statsMode: StatsMode): number =>
	matchingLocations.reduce((sum, loc) => {
		const census = resolveLocationCensus(loc, context.byDimensions, context.totalCensus, statsMode, ALLOW_AVAILABILITY_FALLBACK);
		return sum + scoreAdjustedRequiredForLocation(loc, census, statsMode, context.dayAbbr, { kind: "all" }, context.demoRequired);
	}, 0);

/**
 * Whether a location has a configured staffing target at all — a ratio-ladder rung or a day-amount that
 * requires at least one person. Census-independent, so it distinguishes "0 required today" (has a par) from
 * "no par configured" (never draws a red pill).
 */
export const locationHasConfiguredTarget = (loc: DepartmentLocation): boolean =>
	(loc.ratioLadder?.length ?? 0) > 0 || Object.values(loc.dayAmounts ?? {}).some((dayAmount) => dayAmount.amount_required > 0);
